<template>
  <div>
    <v-menu
      offsetY
      :closeOnContentClick="false"
      :closeDelay="100"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn

          v-bind="attrs"
          color="secondary"
          class="download-button"
          v-on="on"
        >
          {{ $t('graphics.downloadDropDown.download') }} | <v-icon
            right
            dark
          >
            mdi-chevron-down
          </v-icon>
        </v-btn>
      </template>

      <v-list>
        <v-list-item>
          <v-select
            v-model="selectedFormat"
            :items="fileFormats"
            :label="$t('graphics.downloadDropDown.selectFormat')"
          />
        </v-list-item>
        <v-list-item>
          <v-btn
            color="secondary"
            block
            @click="handleDownload"
          >
            {{ $t('graphics.downloadDropDown.downloadGraph') }}
            {{ selectedFormat }}
            <v-icon
              right
            >
              download
            </v-icon>
          </v-btn>
        </v-list-item>
        <div class="divider-or">
          <div class="line" />
          <span class="or-text">
            {{ $t('graphics.downloadDropDown.or') }}
          </span>
          <div class="line" />
        </div>
        <v-list-item>
          <v-btn
            color="secondary"
            block
            @click="downloadAllWrapper"
          >
            {{ $t('graphics.downloadDropDown.downloadAll') }}
            <v-icon
              right
            >
              download
            </v-icon>
          </v-btn>
        </v-list-item>
        <div class="divider-or">
          <div class="line" />
          <span class="or-text">
            {{ $t('graphics.downloadDropDown.or') }}
          </span>
          <div class="line" />
        </div>

        <v-list-item>
          <v-btn
            prependIcon="download"
            color="secondary"
            block
            @click="downloadCSV"
          >
            {{ $t('graphics.downloadDropDown.download') }}
            CSV
            <v-icon
              right
              dark
            >
              download
            </v-icon>
          </v-btn>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';
import { getLegendMetadata } from '@/utilities/graphs';
import { mapGetters } from 'vuex';

export default {
    name: 'DownloadMenu',
    props: {
        graphicsWrapper: {
            type: [Object, Element],
            required: true,
        },
        svgRef: {
            type: [Object, Element],
            required: true,
        },
        seriesData: {
            type: Array,
            required: true,
        },
        series: {
            type: Array,
            required: false,
        },
    },
    data() {
        return {
            fileFormats: ['svg', 'png', 'jpg'],
            selectedFormat: 'svg',
        };
    },
    computed: {
        ...mapGetters(['lang', 'teamMainLang']),
    },
    methods: {
        handleDownload() {
            if (this.selectedFormat === 'svg') {
                this.downloadSVG();
            } else {
                this.downloadImage(this.selectedFormat);
            }
        },
        async downloadAllWrapper() {
            const container = this.graphicsWrapper;
            const clone = container.cloneNode(true);
            const controls = clone.querySelectorAll('.v-window__prev, .v-window__next');
            controls.forEach(el => el.remove());
            const cloneSvgs = clone.querySelectorAll('svg');
            if (cloneSvgs.length > 1) {
                const secondSvg = cloneSvgs[1];
                secondSvg.setAttribute('width', '600px');
                secondSvg.style.width = '600px';
                secondSvg.style.maxWidth = 'none';
                secondSvg.style.overflow = 'visible';
            }
            if (cloneSvgs.length > 0) {
                await this.appendLegendToSVG(cloneSvgs[0]);
                cloneSvgs[0].style.overflow = 'visible';
            }

            clone.style.position = 'absolute';
            clone.style.top = '0';
            clone.style.left = '0';
            clone.style.transform = 'translate(-9999px, 0)';
            clone.style.overflow = 'visible';

            document.body.appendChild(clone);

            setTimeout(() => {
                const rect = clone.getBoundingClientRect();
                clone.style.width = `${rect.width }px`;
                clone.style.height = `${rect.height }px`;

                html2canvas(clone, { useCORS: true })
                    .then(canvas => {
                        document.body.removeChild(clone);

                        const imgData = canvas.toDataURL('image/png');
                        const link = document.createElement('a');
                        link.href = imgData;
                        link.download = 'graphics.png';
                        link.click();
                    })
                    .catch(() => {
                        document.body.removeChild(clone);
                    });
            }, 100);
        },
        async appendLegendToSVG(clonedSvg) {
            const ns = 'http://www.w3.org/2000/svg';
            const legendGroup = document.createElementNS(ns, 'g');
            legendGroup.setAttribute('class', 'legend');

            let originalWidth = parseFloat(clonedSvg.getAttribute('width'));
            if (Number.isNaN(originalWidth)) {
                originalWidth = this.svgRef.clientWidth;
            }

            const extraSpace = 250;
            const viewBox = clonedSvg.getAttribute('viewBox');
            if (viewBox) {
                // eslint-disable-next-line prefer-const
                let [minX, minY, width, height] = viewBox.split(' ').map(Number);
                width += extraSpace;
                clonedSvg.setAttribute('viewBox', `${minX} ${minY} ${width} ${height}`);
            }
            const newWidth = originalWidth + extraSpace;
            clonedSvg.setAttribute('width', newWidth);
            const xOffset = originalWidth - 50;
            const yOffset = 100;
            legendGroup.setAttribute('transform', `translate(${xOffset}, ${yOffset})`);

            const legendData = await getLegendMetadata(this.$rq, this.series, this.lang, this.teamMainLang);
            legendData.forEach((data, i) => {
                const legendItem = document.createElementNS(ns, 'g');
                legendItem.setAttribute('transform', `translate(0, ${i * 20})`);

                const rect = document.createElementNS(ns, 'rect');
                rect.setAttribute('x', '0');
                rect.setAttribute('y', '-10');
                rect.setAttribute('width', '10');
                rect.setAttribute('height', '10');
                rect.setAttribute('fill', data.color);
                legendItem.appendChild(rect);

                const text = document.createElementNS(ns, 'text');
                text.setAttribute('x', '20');
                text.setAttribute('y', '0');
                text.setAttribute('font-size', '12px');
                text.setAttribute('dominant-baseline', 'middle');

                text.textContent = `${data.analysisName}`;

                legendItem.appendChild(text);
                legendGroup.appendChild(legendItem);
            });

            clonedSvg.appendChild(legendGroup);
        },

        async downloadSVG() {
            const svg = this.svgRef;
            const clonedSvg = svg.cloneNode(true);
            await this.appendLegendToSVG(clonedSvg);

            const serializer = new XMLSerializer();
            let svgString = serializer.serializeToString(clonedSvg);

            if (!svgString.includes('xmlns="http://www.w3.org/2000/svg"')) {
                svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
            }
            if (!svgString.includes('xmlns:xlink="http://www.w3.org/1999/xlink"')) {
                svgString = svgString.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
            }

            svgString = `<?xml version="1.0" standalone="no"?>\r\n${svgString}`;

            const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = 'graph.svg';
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);
        },
        async downloadImage(format) {
            const svg = this.svgRef;
            const clonedSvg = svg.cloneNode(true);
            await this.appendLegendToSVG(clonedSvg);

            const serializer = new XMLSerializer();
            let svgString = serializer.serializeToString(clonedSvg);

            if (!svgString.includes('xmlns="http://www.w3.org/2000/svg"')) {
                svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
            }
            if (!svgString.includes('xmlns:xlink="http://www.w3.org/1999/xlink"')) {
                svgString = svgString.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
            }

            const img = new Image();
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            img.onload = () => {
                const widthAttr = clonedSvg.getAttribute('width');
                const heightAttr = clonedSvg.getAttribute('height');
                const width = widthAttr ? parseFloat(widthAttr) : svg.clientWidth;
                const height = heightAttr ? parseFloat(heightAttr) : svg.clientHeight;

                const scaleFactor = 2;

                const canvas = document.createElement('canvas');
                canvas.width = width * scaleFactor;
                canvas.height = height * scaleFactor;

                const context = canvas.getContext('2d');

                if (format === 'jpg') {
                    context.fillStyle = '#ffffff';
                    context.fillRect(0, 0, canvas.width, canvas.height);
                }

                context.scale(scaleFactor, scaleFactor);
                context.drawImage(img, 0, 0, width, height);
                const imageType = format === 'jpg' ? 'image/jpeg' : 'image/png';
                const imgData = canvas.toDataURL(imageType, 1.0);

                const downloadLink = document.createElement('a');
                downloadLink.href = imgData;
                downloadLink.download = `graph.${format}`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);

                URL.revokeObjectURL(url);
            };

            img.src = url;
        },
        downloadCSV() {
            const header = ['Series Name', 'Measured At', 'Value', 'Unit'];
            this.$utils.exportSimpleCSV(header, this.seriesData, 'series_data.csv', 'graph');
        },
    },
};
</script>

<style>
  .download-button {
      margin: 10px;
    }

    .v-menu .v-list {
        width: 200px;
    }

    .v-select {
        width: 100%;
    }
    .divider-or {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
}

.line {
  height: 1px;
  background-color: #ccc;
  flex-grow: 1;
}

.or-text {
  margin: 0 12px;
  color: #666;
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
}
</style>
